/**
 * Client hooks for Computer **environments** (sandbox images).
 *
 * sphinx has no computer/sandbox control plane: these were Convex functions
 * on the mcpjam backend. The query hooks resolve to "none" (`undefined` →
 * consumers render their empty/hidden state) and the mutations reject with a
 * clear message. Types are kept because eval/computer components import them.
 */

export type BuildStatus = "queued" | "building" | "ready" | "failed";

export interface SandboxImageBuildView {
  buildId: string;
  status: BuildStatus;
  provider: "e2b" | "stub";
  e2bBuildId?: string;
  baseImageDigests: string[];
  logPreview?: string;
  error?: string;
  createdAt: number;
  startedAt?: number;
  finishedAt?: number;
}

export interface SandboxImageView {
  environmentId: string;
  projectId: string;
  name: string;
  blueprint: string;
  contentHash: string;
  sharing: "user" | "project";
  /** True only for a per-user draft the caller owns. */
  isOwner: boolean;
  currentBuildId?: string;
  currentBuild: SandboxImageBuildView | null;
  createdAt: number;
  updatedAt: number;
}

const NO_IMAGES: SandboxImageView[] = [];
const NO_BUILDS: SandboxImageBuildView[] = [];

/** Visible environments for a project. */
export function useSandboxImages(
  projectId: string | null
): SandboxImageView[] | undefined {
  return projectId ? NO_IMAGES : undefined;
}

/** One environment (with its current build), or `null` if not visible. */
export function useSandboxImage(
  environmentId: string | null
): SandboxImageView | null | undefined {
  return environmentId ? null : undefined;
}

/** All builds for an environment, newest first. */
export function useSandboxImageBuilds(
  environmentId: string | null
): SandboxImageBuildView[] | undefined {
  return environmentId ? NO_BUILDS : undefined;
}

export type BlueprintValidationResult =
  | { ok: true; baseImageDigest: string }
  | { ok: false; errors: { path: string; message: string }[] };

/** Lint blueprint YAML without saving. No validator in sphinx → pending. */
export function useValidateBlueprint(
  _projectId: string | null,
  _blueprint: string | null
): BlueprintValidationResult | undefined {
  return undefined;
}

const unsupported = async (..._args: unknown[]): Promise<never> => {
  throw new Error("Sandbox images are not available in sphinx.");
};

export function useCreateSandboxImage(): (args: {
  projectId: string;
  name: string;
  blueprint: string;
}) => Promise<SandboxImageView> {
  return unsupported;
}

export function useUpdateSandboxImage(): (args: {
  environmentId: string;
  name?: string;
  blueprint?: string;
}) => Promise<SandboxImageView> {
  return unsupported;
}

export function useStartSandboxImageBuild(): (args: {
  environmentId: string;
}) => Promise<{ buildId: string; reused: boolean }> {
  return unsupported;
}

export function usePromoteSandboxImage(): (args: {
  environmentId: string;
}) => Promise<SandboxImageView> {
  return unsupported;
}

export function useDeleteSandboxImage(): (args: {
  environmentId: string;
}) => Promise<{ deleted: true }> {
  return unsupported;
}

/** Attach an environment to the caller's computer (or detach with `null`). */
export function useSetComputerSandboxImage(): (args: {
  projectId: string;
  environmentId: string | null;
}) => Promise<{ computerId: string; status: string; environmentId?: string }> {
  return unsupported;
}

/** Reset the computer to its image (wipes mutable state). */
export function useResetComputer(): (args: {
  projectId: string;
}) => Promise<{ reset: boolean }> {
  return unsupported;
}
